import { useFormikContext } from 'formik'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { DatePicker, Select } from '@loadsmart/loadsmart-ui'
import {
  Button,
  Field,
  Layout,
  Text,
  TextArea,
  TextField,
  ToggleGroup,
  type ToggleGroupChangeEventDetail,
} from '@loadsmart/miranda-react'

import {
  AppointmentLoadingTypeOptions,
  AppointmentModeOptions,
  AppointmentTimeOptions,
  type StopAppointment,
} from 'core/appointments/domain/Appointment'
import { usePasteFromClipboard } from 'core/ui/hooks/usePasteFromClipboard'

import { hasAppointmentReason } from '../../domain/Appointment'
import { type AppointmentFormValues } from '../../infra/ShipmentRepository'
import { APPOINTMENT_MISSCHEDULING_REASON_OPTIONS } from '../../presenters/appointment'

const FullSizeDatePicker = styled.div`
  width: 100%;
  [role='menubar'] > div > div > div {
    width: 100%;
  }
`

export const APPOINTMENT_TIME_SELECT_OPTIONS = [
  { label: 'Specific time', value: AppointmentTimeOptions.At },
  { label: 'Between', value: AppointmentTimeOptions.Between },
]

export const APPOINTMENT_MODE_OPTIONS = [
  { label: 'Appointment', value: AppointmentModeOptions.Appt },
  { label: 'FCFS', value: AppointmentModeOptions.Fcfs },
]

export const APPOINTMENT_LOADING_TYPE_OPTIONS = [
  { label: 'Live', value: AppointmentLoadingTypeOptions.Live },
  { label: 'Drop', value: AppointmentLoadingTypeOptions.Drop },
]

type RegularAppointmentDrawerFormProps = {
  appointment?: StopAppointment
}

export function RegularAppointmentDrawerForm({ appointment }: RegularAppointmentDrawerFormProps) {
  const { values, setFieldValue, handleChange, errors } = useFormikContext<AppointmentFormValues>()

  const showAppointmentReason = hasAppointmentReason(values, appointment)

  const { paste } = usePasteFromClipboard({
    onSuccess: pastedText => {
      setFieldValue('confirmationNumber', pastedText)
      toast.success('Confirmation number pasted!', { position: 'top-left' })
    },
  })

  function handleToggleChange(event: CustomEvent<ToggleGroupChangeEventDetail>, fieldName: string) {
    setFieldValue(fieldName, event.detail.value)
  }

  return (
    <Layout.Stack style={{ marginTop: '16px' }} gap="spacing-4">
      <Layout.Stack gap="spacing-1">
        <Text variant="body-md" color="color-text-tertiary">
          <label htmlFor="date">Appointment date</label>
        </Text>
        <FullSizeDatePicker>
          <DatePicker id="date" name="date" value={values.date} onChange={handleChange} />
        </FullSizeDatePicker>
      </Layout.Stack>

      <Layout.Group>
        <Layout.Stack gap="spacing-1">
          <Text variant="body-md" color="color-text-tertiary">
            Type
          </Text>
          <ToggleGroup
            id="mode"
            type="single-strict"
            value={values.mode}
            options={APPOINTMENT_MODE_OPTIONS}
            onChange={e => handleToggleChange(e, 'mode')}
          />
        </Layout.Stack>
        <Layout.Stack gap="spacing-1">
          <Text variant="body-md" color="color-text-tertiary">
            Loading type
          </Text>
          <ToggleGroup
            id="loadingType"
            type="single-strict"
            value={values.loadingType}
            options={APPOINTMENT_LOADING_TYPE_OPTIONS}
            onChange={e => handleToggleChange(e, 'loadingType')}
          />
        </Layout.Stack>
      </Layout.Group>

      <Layout.Stack gap="spacing-1">
        <Text variant="body-md" color="color-text-tertiary">
          Time
        </Text>
        <Layout.Grid gap="spacing-1" minColumnWidth="30%">
          <Select
            name="timeType"
            options={APPOINTMENT_TIME_SELECT_OPTIONS}
            value={values.timeType}
            onChange={handleChange}
            hideClear
          />
          <TextField
            id="time"
            type="text"
            value={values.time}
            maxLength={4}
            placeholder="e.g. 1300"
            aria-label="time"
            pattern="[0-9]{4}"
            onInput={handleChange}
          />
          {values.timeType?.value === 'between' && (
            <TextField
              id="timeEnd"
              type="text"
              value={values.timeEnd}
              maxLength={4}
              placeholder="e.g. 1300"
              aria-label="timeEnd"
              pattern="[0-9]{4}"
              onInput={handleChange}
            />
          )}
        </Layout.Grid>
        {(errors.timeEnd || errors.time || errors.timeType) && (
          <Text variant="body-sm" color="color-danger-100">
            {errors.timeEnd || errors.time || errors.timeType}
          </Text>
        )}
      </Layout.Stack>

      <Layout.Stack gap="spacing-1">
        <Text variant="body-md" color="color-text-tertiary">
          Confirmation number (optional)
        </Text>
        <Layout.Group align="center" justify="space-between">
          <TextField
            id="confirmationNumber"
            name="confirmationNumber"
            aria-label="confirmationNumber"
            value={values.confirmationNumber ?? ''}
            style={{ flex: 1 }}
            onInput={handleChange}
          />
          <Button type="button" onClick={paste} variant="tertiary" size="small">
            Paste code
          </Button>
        </Layout.Group>
        {errors.confirmationNumber && (
          <Text variant="body-sm" color="color-danger-100">
            {errors.confirmationNumber}
          </Text>
        )}
      </Layout.Stack>

      {appointment && showAppointmentReason && (
        <Layout.Stack gap="spacing-4">
          {appointment.status === 'pending' && (
            <Text variant="body-sm" color="color-warning-100">
              Selected time or date falls outside the client's suggested schedule. Please provide a
              reason.
            </Text>
          )}
          <Field required>
            <Field.Label color="color-text-tertiary">Reschedule reason</Field.Label>
            <Select
              name="misschedulingReason"
              placeholder="Select reason"
              options={APPOINTMENT_MISSCHEDULING_REASON_OPTIONS}
              value={values.misschedulingReason}
              onChange={handleChange}
              hideClear
            />
          </Field>
          <Field
            label="Note (max 100 characters)"
            required={values.misschedulingReason?.value === 'other'}
          >
            <TextArea
              name="misschedulingNote"
              value={values.misschedulingNote}
              onInput={handleChange}
              aria-label="note"
              resize="none"
              maxLength={100}
            />
          </Field>
        </Layout.Stack>
      )}
    </Layout.Stack>
  )
}
